import {Action, createReducer, on} from '@ngrx/store';
import {initialStorageState, StorageState} from '@store/state/storage.state';
import {
    articleTemplate,
    deleteReadNews,
    deselectCustomer,
    resetAccessExpire,
    resetDeliveryArea,
    resetShoppingCart,
    resetTokensExpire,
    selectCustomer,
    setAccessExpire,
    setDeliveryArea,
    setInjectionToken,
    setReadNews,
    setRefreshExpire,
    setSelectedBillingAddress,
    setSelectedDeliveryAddress,
    shoppingCart,
    shoppingCartDeliveryType,
    shoppingCartDynamicArticleIdsCustomer,
    shoppingCartDynamicArticleIdsDeselect, shoppingCartTourDynamicArticleIdsTour,
    showDeliveryAreaSelection,
    userId
} from '@store/actions/storage.action';

export const initialState: StorageState = initialStorageState();

const StorageReducer = createReducer(
        initialState,
        on(setAccessExpire, (state: StorageState, {expiredIn}): StorageState => {
                return {
                    ...state,
                    tokens: {
                        refresh: state.tokens.refresh,
                        access: {
                            expiredIn: expiredIn,
                            createdTimestampInSeconds: Date.now() / 1000
                        }
                    }
                }
            }
        ),
        on(setRefreshExpire, (state: StorageState, {expiredIn}): StorageState => {
                return {
                    ...state,
                    tokens: {
                        access: state.tokens.access,
                        refresh: {
                            expiredIn: expiredIn,
                            createdTimestampInSeconds: Date.now() / 1000
                        }
                    }
                }
            }
        ),
        on(setInjectionToken, (state: StorageState, {token, expiredIn}): StorageState => {
                return {
                    ...state,
                    tokens: {
                        access: {
                            injectionToken: token,
                            expiredIn,
                            createdTimestampInSeconds: Date.now() / 1000
                        },
                        refresh: null
                    }
                }
            }
        ),
        on(resetAccessExpire, (state: StorageState): StorageState => {
                return {
                    ...state,
                    tokens: {
                        access: null,
                        refresh: null
                    }
                }
            }
        ),
        on(resetTokensExpire, (state: StorageState): StorageState => {
                return {
                    ...state,
                    tokens: {
                        access: null,
                        refresh: null
                    }
                }
            }
        ),
        on(setReadNews, (state: StorageState, {id}): StorageState => {
                return {
                    ...state,
                    readNews: [...state.readNews.filter(r => r != id), id]
                }
            }
        ),
        on(deleteReadNews, (state: StorageState, {withoutIds}): StorageState => {
                return {
                    ...state,
                    readNews: state.readNews.filter(r => withoutIds.includes(r))
                }
            }
        ),
        on(shoppingCart, (state: StorageState, {id, token}): StorageState => {
                return {
                    ...state,
                    shoppingCart: {
                        id,
                        token,
                        deliveryType: state.shoppingCart?.deliveryType || undefined,
                        dynamicArticleIds: state.shoppingCart?.dynamicArticleIds
                    }
                }
            }
        ),
        on(shoppingCartDeliveryType, (state: StorageState, {deliveryType}): StorageState => {
                return {
                    ...state,
                    shoppingCart: {
                        id: state.shoppingCart?.id,
                        token: state.shoppingCart?.token,
                        deliveryType,
                        dynamicArticleIds: state.shoppingCart?.dynamicArticleIds
                    }
                }
            }
        ),
        on(shoppingCartDynamicArticleIdsDeselect, (state: StorageState, {ids}): StorageState => {

                return {
                    ...state,
                    shoppingCart: {
                        id: state.shoppingCart?.id,
                        token: state.shoppingCart?.token,
                        deliveryType: state.shoppingCart?.deliveryType,
                        dynamicArticleIds: {
                            deselected: ids,
                            tour: state.shoppingCart?.dynamicArticleIds?.tour,
                            customer: state.shoppingCart?.dynamicArticleIds?.customer,
                        }
                    }
                }
            }
        ),
        on(shoppingCartDynamicArticleIdsCustomer, (state: StorageState, {ids}): StorageState => {
                return {
                    ...state,
                    shoppingCart: {
                        id: state.shoppingCart?.id,
                        token: state.shoppingCart?.token,
                        deliveryType: state.shoppingCart?.deliveryType,
                        dynamicArticleIds: {
                            customer: ids,
                            tour: state.shoppingCart?.dynamicArticleIds?.tour,
                            deselected: state.shoppingCart?.dynamicArticleIds?.deselected,
                        }
                    }
                }
            }
        ),
        on(shoppingCartTourDynamicArticleIdsTour, (state: StorageState, {ids}): StorageState => {
                return {
                    ...state,
                    shoppingCart: {
                        id: state.shoppingCart?.id,
                        token: state.shoppingCart?.token,
                        deliveryType: state.shoppingCart?.deliveryType,
                        dynamicArticleIds: {
                            tour: ids,
                            customer: state.shoppingCart?.dynamicArticleIds?.customer,
                            deselected: state.shoppingCart?.dynamicArticleIds?.deselected,
                        }
                    }
                }
            }
        ),
        on(resetShoppingCart, (state: StorageState): StorageState => {
                return {
                    ...state,
                    shoppingCart: null
                }
            }
        ),
        on(articleTemplate, (state: StorageState, {articleTemplate}): StorageState => {
                return {
                    ...state,
                    articleTemplate
                }
            }
        ),
        on(userId, (state: StorageState, {userId}): StorageState => {
                return {
                    ...state,
                    userId
                }
            }
        ),
        on(showDeliveryAreaSelection, (state: StorageState, {value}): StorageState => {
                return {
                    ...state,
                    userPostcode: state.userPostcode,
                    userTown: state.userTown,
                    showDeliveryAreaSelection: value
                }
            }
        ),
        on(setDeliveryArea, (state: StorageState, {postcode, town}): StorageState => {
                return {
                    ...state,
                    userPostcode: postcode,
                    userTown: town,
                    showDeliveryAreaSelection: false
                }
            }
        ),
        on(resetDeliveryArea, (state: StorageState): StorageState => {
                return {
                    ...state,
                    userPostcode: undefined,
                    userTown: undefined,
                    showDeliveryAreaSelection: true
                }
            }
        ),
        on(selectCustomer, (state: StorageState, {customerId}): StorageState => {
            return {
                ...state,
                ...{
                    customer: {
                        id: customerId,
                        selectedBillingAddressId: state.customer?.selectedBillingAddressId ?? '',
                        selectedDeliveryAddressId: state.customer?.selectedDeliveryAddressId ?? ''
                    }
                }
            };
        }),
        on(setSelectedBillingAddress, (state: StorageState, {customerId, addressId}): StorageState => {
            return {
                ...state,
                ...{
                    customer: {
                        id: customerId,
                        selectedBillingAddressId: addressId,
                        selectedDeliveryAddressId: state.customer?.selectedDeliveryAddressId ?? ''
                    }
                }
            };
        }),
        on(setSelectedDeliveryAddress, (state: StorageState, {customerId, addressId}): StorageState => {
            return {
                ...state,
                ...{
                    customer: {
                        id: customerId,
                        selectedDeliveryAddressId: addressId,
                        selectedBillingAddressId: state.customer?.selectedBillingAddressId ?? ''
                    }
                }
            };
        }),
        on(deselectCustomer, (state: StorageState): StorageState => {
            return {
                ...state,
                ...{
                    customer: undefined
                }
            };
        }),
    )
;

export function reducer(state: StorageState | undefined, action: Action) {
    return StorageReducer(state, action);
}
